import type { IObservableArray } from 'mobx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Campaign, CampaignClass, type Targeting } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { useFlags, useStore } from '@feathr/extender/state';

interface IUseReconcileUniqueEmailsProps {
  targetings: IObservableArray<Targeting>;
  minRequiredEmails?: number;
  campaign: Campaign;
}

export interface IUniqueEmailsResult {
  numUniqueEmails: number | null;
  hasEnoughUniqueEmails: boolean;
  remainingEmails: number;
  isLoading: boolean;
}

const MIN_NUM_EMAILS = {
  [CampaignClass.EmailListFacebook]: 2500,
  [CampaignClass.EmailList]: 2000,
};

export function useReconcileUniqueEmails({
  targetings,
  minRequiredEmails,
  campaign,
}: IUseReconcileUniqueEmailsProps): IUniqueEmailsResult {
  const { Targetables, Segments } = useStore();
  const { noMinAudience } = useFlags();
  const [numUniqueEmails, setNumUniqueEmails] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { isEmailMappingCampaign } = campaign;

  // Get included email list targetings
  const includedEmail = targetings.filter(
    (t) => !t.get('is_archived') && t.get('kind') === 'email_list' && t.get('included'),
  );

  // Get included email list IDs
  const includedEmailIds = includedEmail
    .map((t) => t.get('target_data'))
    .filter(Boolean) as string[];

  const includedEmailLists = includedEmailIds
    .map((id) => {
      const targetable = Targetables.get(id);
      return targetable?.get('list_file');
    })
    .filter(Boolean) as string[];

  // Get included segment IDs
  const includedSegmentIds = targetings
    .filter(
      (t) =>
        !t.get('is_archived') &&
        t.get('kind') === 'segment' &&
        t.get('included') &&
        // Only include non-ephemeral segments saved
        !Segments.get(t.get('target_data')!)?.isEphemeral,
    )
    .map((t) => t.get('target_data'))
    .filter(Boolean) as string[];

  const excludedSegmentIds = targetings
    .filter((t) => !t.get('is_archived') && t.get('kind') === 'segment' && !t.get('included'))
    .map((t) => t.get('target_data'))
    .filter(Boolean) as string[];

  // Stringify the arrays so they can be used as reliable deps
  const deps =
    JSON.stringify(includedSegmentIds) +
    JSON.stringify(includedEmailLists) +
    JSON.stringify(excludedSegmentIds) +
    JSON.stringify(includedEmailIds);

  // Only process if targetings exist
  useEffect(() => {
    async function getNumUniqueEmails(): Promise<void> {
      setIsLoading(true);

      try {
        const response = await Segments.getUniqueEmailsFromTargetables({
          email_list_urls: includedEmailLists,
          excluded_segment_ids: excludedSegmentIds,
          included_segment_ids: includedSegmentIds,
        });

        setNumUniqueEmails(response.unique_emails);
      } catch (error) {
        toast(
          t("We couldn't deduplicate your email list due to a connection error. Try again later.", {
            error,
          }),
          {
            type: 'error',
          },
        );
        setNumUniqueEmails(null);
      } finally {
        setIsLoading(false);
      }
    }

    // We only need to reconcile unique emails for facebook email mapping campaigns
    if (
      isEmailMappingCampaign &&
      (includedEmailIds.length || includedSegmentIds.length || excludedSegmentIds.length)
    ) {
      // Only make the API call if we have email lists loaded OR if we only have segments
      if (
        includedEmailLists.length > 0 ||
        (includedEmailIds.length === 0 && includedSegmentIds.length > 0)
      ) {
        getNumUniqueEmails();
      } else {
        // Reset loading state if email lists aren't ready yet
        setIsLoading(false);
      }
    } else {
      // Reset state if no targetings to process
      setNumUniqueEmails(null);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deps]);

  const numRequiredEmails = minRequiredEmails ?? MIN_NUM_EMAILS[campaign.get('_cls')];
  // The noMinAudience flag turns off the minimum-audience gate (unique-email floor
  // for email-mapping campaigns), mirroring its reach-minimum bypass in the wizard.
  const hasEnoughUniqueEmails =
    !!noMinAudience || Boolean(numUniqueEmails && numUniqueEmails >= numRequiredEmails);
  const remainingEmails = numUniqueEmails ? numRequiredEmails - numUniqueEmails : numRequiredEmails;

  return {
    numUniqueEmails,
    hasEnoughUniqueEmails,
    remainingEmails,
    isLoading,
  };
}
